import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Building2,
  FileText,
  User,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Send,
  MessageSquare,
  ClipboardCheck,
} from 'lucide-react';
import { SupplierQualification } from '../../lib/api';
import { format } from 'date-fns';

interface OverviewTabProps {
  qualification: SupplierQualification;
  isCustomer: boolean;
  isSupplier: boolean;
}

export function OverviewTab({ qualification, isCustomer: _isCustomer, isSupplier: _isSupplier }: OverviewTabProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Qualification Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Qualification Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center gap-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm text-muted-foreground">Template</div>
              <div className="font-medium">{qualification.template_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm text-muted-foreground">Supplier</div>
              <div className="font-medium">{qualification.supplier_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <User className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm text-muted-foreground">Customer</div>
              <div className="font-medium">{qualification.customer_name}</div>
            </div>
          </div>
          {qualification.notes && (
            <div className="flex items-start gap-3">
              <MessageSquare className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <div className="text-sm text-muted-foreground">Notes</div>
                <div className="font-medium">{qualification.notes}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Dates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Key Dates
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          {qualification.invited_at && (
            <div className="flex items-center gap-3">
              <Send className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Invited</div>
                <div className="font-medium">
                  {format(new Date(qualification.invited_at), 'MMM d, yyyy h:mm a')}
                  {qualification.invited_by_name && (
                    <span className="text-muted-foreground ml-1">by {qualification.invited_by_name}</span>
                  )}
                </div>
              </div>
            </div>
          )}
          {qualification.started_at && (
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Started</div>
                <div className="font-medium">
                  {format(new Date(qualification.started_at), 'MMM d, yyyy h:mm a')}
                </div>
              </div>
            </div>
          )}
          {qualification.submitted_at && (
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Submitted</div>
                <div className="font-medium">
                  {format(new Date(qualification.submitted_at), 'MMM d, yyyy h:mm a')}
                  {qualification.submitted_by_name && (
                    <span className="text-muted-foreground ml-1">by {qualification.submitted_by_name}</span>
                  )}
                </div>
              </div>
            </div>
          )}
          {qualification.approved_at && (
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <div>
                <div className="text-sm text-muted-foreground">Approved</div>
                <div className="font-medium">
                  {format(new Date(qualification.approved_at), 'MMM d, yyyy h:mm a')}
                  {qualification.approved_by_name && (
                    <span className="text-muted-foreground ml-1">by {qualification.approved_by_name}</span>
                  )}
                </div>
              </div>
            </div>
          )}
          {qualification.expiry_date && (
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <div>
                <div className="text-sm text-muted-foreground">Expires</div>
                <div className="font-medium">
                  {format(new Date(qualification.expiry_date), 'MMM d, yyyy')}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
